/**
 * Utility functions for cleaning up application state
 */

import { signOut } from "next-auth/react";
import { mutate } from "swr";
import { createLogger } from "@/app/lib/logger";
import { getLocaleFromPath, addLocaleToPath } from "@/app/lib/i18n-config";

const log = createLogger("Cleanup");

// Track if we're currently clearing session to prevent redundant calls
let isClearingSession = false;

/**
 * Session storage keys used by the auth system that should be cleared on logout.
 */
const AUTH_SESSION_KEYS = [
  "login-timestamp",
  "session-validation-in-progress",
  "session-just-cleared",
  "session-sync-reload",
  "hasPendingJob",
  "prefill-password",
  "verification-access-token",
] as const;

/**
 * SWR cache keys that should be cleared on logout.
 */
const SWR_CACHE_KEYS = [
  "current-user",
] as const;

/**
 * Clear all local storage and session storage
 */
export const clearAllStorage = () => {
  if (typeof window !== "undefined") {
    localStorage.clear();
    sessionStorage.clear();
  }
};

/**
 * Clear NextAuth session data more thoroughly
 */
export const clearAuthSession = () => {
  if (typeof window !== "undefined") {
    // Prevent redundant session clearing
    if (isClearingSession) {
      log.debug("Session clearing already in progress, skipping");
      return;
    }

    isClearingSession = true;
    log.debug("Clearing auth session");

    // Clear specific NextAuth cookies only
    const cookiesToClear = [
      "next-auth.session-token",
      "next-auth.csrf-token",
      "next-auth.callback-url",
      "__Secure-next-auth.session-token",
      "__Secure-next-auth.csrf-token",
      "__Secure-next-auth.callback-url",
    ];

    cookiesToClear.forEach(cookieName => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
    });

    // Clear any stored session data
    localStorage.removeItem("next-auth.session-token");
    sessionStorage.removeItem("next-auth.session-token");

    // Reset the flag after a short delay
    setTimeout(() => {
      isClearingSession = false;
    }, 1000);
  }
};

/**
 * Unified session clearing and logout function.
 * Performs a thorough cleanup of all auth-related state:
 * 1. Clears auth-specific sessionStorage keys
 * 2. Clears SWR cache for user data
 * 3. Calls NextAuth signOut
 */
export const clearSessionAndRedirect = async (message?: string) => {
  if (typeof window !== "undefined") {
    log.debug("Unified session clearing and redirect");

    // Get current locale from path BEFORE clearing anything
    const currentLocale = getLocaleFromPath(window.location.pathname);

    // Build redirect URL before clearing storage
    const loginPath = addLocaleToPath("/login", currentLocale);
    const redirectUrl = message
      ? `${loginPath}?message=${encodeURIComponent(message)}`
      : loginPath;

    // Clear auth-specific sessionStorage keys
    AUTH_SESSION_KEYS.forEach(key => {
      try {
        sessionStorage.removeItem(key);
      } catch {
        // Ignore errors
      }
    });

    // Clear SWR cache for user data
    for (const key of SWR_CACHE_KEYS) {
      try {
        await mutate(key, undefined, { revalidate: false });
      } catch {
        // Ignore errors
      }
    }

    // Clear email verification banner dismiss state
    try {
      localStorage.removeItem("emailVerificationBannerDismissed");
    } catch {
      // Ignore errors
    }

    // IMPORTANT: Let signOut() handle cookie clearing - don't clear before calling it
    // signOut needs the session cookies to properly invalidate the session
    await signOut({
      callbackUrl: redirectUrl,
      redirect: true,
    });

    // Note: Code after signOut with redirect:true won't execute because of redirect
  }
};

/**
 * Validate if the current session is still valid by making a test API call
 */
export const validateSession = async (): Promise<boolean> => {
  try {
    // Make a simple authenticated request to test if the session is still valid
    const response = await fetch("/api/auth/validate", {
      method: "GET",
      credentials: "include",
    });

    return response.ok;
  } catch (error) {
    log.error("Session validation failed", error);
    return false;
  }
};

/**
 * Reset application state
 */
export const resetApplicationState = () => {
  clearSessionAndRedirect("Session cleared. Please sign in again.");
};

/**
 * Handle authentication errors and redirect to login
 */
export const handleAuthError = (error: unknown) => {
  log.error("Authentication error", error);
  clearSessionAndRedirect("Your session has expired. Please sign in again.");
};
